import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormInstance } from 'antd/es/form';
import { get } from 'lodash';
import { BiMessageCheck } from 'react-icons/bi';
import AvailableScheduleModal from 'components/user/AvailableScheduleModal';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { updateMentor, getCurrentMentor, activateMentoring, updateIntroduction } from 'libs/api/user';
import { useDataApi } from 'libs/hooks';
import { showNotification } from 'libs/utils';
import { TMentorUpdate, TUser, TMentor, setUserInfo, TIntroductionUpdate } from 'modules/user';
import { Switch, Divider, Button, Form, Input, Typography, Row } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
type TMentorEditProps = {
  form: FormInstance;
};
const MentorEdit = ({ form }: TMentorEditProps) => {
  const dispatch = useDispatch();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  function onClickSubmit() {
    form
      .validateFields()
      .then((value) => {
        (async (value: TMentorUpdate) => {
          try {
            setSubmitButtonDisabled(true);
            const response = await updateMentor(value);
            dispatch(setUserInfo(get(response, 'data.user') as TUser));

            showNotification('success', '회원정보가 수정되었습니다');
          } catch (e) {
            handleFieldError(e, form);
            throw e;
          } finally {
            setSubmitButtonDisabled(false);
          }
        })(value as TMentorUpdate);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  }

  return (
    <Input.Group className='form-inline' style={{ marginTop: '20px' }}>
      <Divider />
      <Form.Item shouldUpdate={true} className='form-button'>
        {() => (
          <Button
            type='primary'
            htmlType='submit'
            size='large'
            style={{ float: 'right', maxWidth: '100px' }}
            onClick={onClickSubmit}
            loading={submitButtonDisabled}
          >
            저장
          </Button>
        )}
      </Form.Item>
    </Input.Group>
  );
};

type TScheduleEditProps = {
  form: FormInstance;
};
export const ScheduleEdit = ({ form }: TScheduleEditProps) => {
  const [{ data, loading }] = useDataApi<TMentor>(getCurrentMentor.bind(null));
  const [activateChecked, setActivateChecked] = useState(false);
  const [availableScheduleModal, setAvailableScheduleModal] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      activate_mentoring: get(data, 'activate_mentoring'),
      introduction: get(data, 'introduction'),
      short_introduction: get(data, 'short_introduction'),
    });
    setActivateChecked(get(data, 'activate_mentoring'));
  }, [data]);

  function handleActivateChecked(checked: boolean) {
    setActivateChecked(checked);
    activateMentoring({ activate_mentoring: checked });
  }

  function onClickSubmit() {
    form.validateFields().then((value) => {
      handleSubmit(value as TIntroductionUpdate);
    });

    async function handleSubmit(value: TIntroductionUpdate) {
      try {
        setSubmitButtonDisabled(true);
        const response = await updateIntroduction({
          introduction: value.introduction,
          short_introduction: value.short_introduction,
        } as TIntroductionUpdate);
        showNotification('success', '자기소개가 수정되었습니다');
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  }

  return (
    <Input.Group className='form-inline'>
      <div className='content-title'>수업 설정</div>
      <BiMessageCheck style={{ color: '#fbbc05' }} />
      <div style={{ color: '#000', fontSize: '12px' }}>
        • 튜터 등록 활성화 상태로 설정하시려면, 아래 항목을 작성하신 후 스위치를 켜주세요. 활성화를 하셔야
        멘티가 수업을 신청할 수 있습니다.
      </div>
      {/* <div style={{ color: "#000", fontSize: "12px", marginBottom: "15px" }}>
        • 현재는 McKinsey, BCG, Bain 분들만 멘토로 모집하고 있습니다. 최대한
        빠른 시일 내에 서비스를 확대하고, 안내 드리겠습니다.
      </div> */}
      <Form.Item className='form-item' noStyle={true}>
        <Row>
          <Form.Item name={'activate_mentoring'}>
            <Switch
              checked={activateChecked}
              onChange={handleActivateChecked}
              checkedChildren='ON'
              unCheckedChildren='OFF'
            />
          </Form.Item>
          <Form.Item>
            <Text style={{ fontSize: '12px' }} strong>
              멘토 등록 설정하기.
            </Text>
          </Form.Item>
        </Row>
      </Form.Item>
      <Row style={{ color: '#000', marginBottom: '40px' }} align='middle'>
        수업 가능 일정&nbsp;&nbsp;&nbsp;
        <Button
          onClick={() => {
            setAvailableScheduleModal(true);
          }}
        >
          <CalendarOutlined /> 수정하기
        </Button>
      </Row>
      <div className='content-title'>자기소개</div>
      <Form.Item name={'short_introduction'} label='한줄 소개' className='form-item'>
        <Input maxLength={12} placeholder='한줄 소개를 입력해주세요. 최대 12자' size='large' />
      </Form.Item>
      <Form.Item name={'introduction'} label='자기 소개' className='form-item'>
        <Input.TextArea
          className='form_textarea'
          placeholder='자기소개를 입력해주세요. 최대 200자'
          autoSize={{ minRows: 6 }}
        />
      </Form.Item>
      <Form.Item shouldUpdate={true} style={{ marginBottom: '30px' }}>
        {() => (
          <Button htmlType='submit' onClick={onClickSubmit} loading={submitButtonDisabled}>
            수정하기
          </Button>
        )}
      </Form.Item>
      <Divider />
      <AvailableScheduleModal
        mentor={data}
        visible={availableScheduleModal}
        setVisible={setAvailableScheduleModal}
      />
    </Input.Group>
  );
};

export default MentorEdit;
